<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-7">
					<h3 class="page-header">
						<i class="fa fa-book animated bounceInDown show-info"></i>
						{{ employee.name || 'Employee' }}
						<small v-if="employee.employee_code" class="text-muted">
							· {{ employee.employee_code }}
						</small>
					</h3>
				</div>
				<div class="col-md-5 text-right" style="margin-top:20px">
					<button class="btn btn-default" @click="back">Back</button>
				</div>
			</div>
		</div>

		<div class="col-md-12" v-if="error">
			<div class="alert alert-danger">{{ error }}</div>
		</div>

		<div class="col-md-12">
			<div class="row stock_section">
				<div class="col-md-3">
					<label>From</label>
					<input type="date" class="form-control form-cascade-control input-box-short" v-model="fromdate" />
				</div>
				<div class="col-md-3">
					<label>To</label>
					<input type="date" class="form-control form-cascade-control input-box-short" v-model="todate" />
				</div>
				<div class="col-md-2" style="margin-top:24px">
					<button class="btn btn-primary" @click="refresh">Show</button>
				</div>
				<div class="col-md-4" style="margin-top:20px">
					<div>
						<span class="text-muted">Salary payable</span>
						<strong style="margin-left:8px">{{ money(salaryBalance) }}</strong>
					</div>
					<div>
						<span class="text-muted">Advance outstanding</span>
						<strong style="margin-left:8px" :class="{ 'text-danger': advanceOutstanding > 0 }">
							{{ money(advanceOutstanding) }}
						</strong>
					</div>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<div class="panel">
				<div class="panel-body" style="background:none">
					<h5>Statement</h5>
					<!--
						The running balance is carried across the whole account, not
						just the rows shown, so narrowing the dates does not restart
						it from zero.
					-->
					<table class="table table-bordered table-hover table-striped display">
						<thead class="table-font" style="background:#475c78;color:#fff">
							<th>#</th>
							<th>Date</th>
							<th>Entry</th>
							<th>Narration</th>
							<th class="text-right">Salary</th>
							<th class="text-right">Payment</th>
							<th class="text-right">Balance</th>
						</thead>
						<tbody class="table-font">
							<tr v-for="(row, index) in entries" :key="row.id">
								<td>{{ index + 1 }}</td>
								<td>{{ formatDate(row.entry_date) }}</td>
								<td>{{ entryLabel(row.entry_type) }}</td>
								<td class="text-muted">{{ row.narration || '' }}</td>
								<td class="text-right" :class="{ 'text-danger': salaryOf(row) < 0 }">
									{{ signedMoney(salaryOf(row)) }}
								</td>
								<td class="text-right" :class="{ 'text-danger': paymentOf(row) < 0 }">
									{{ signedMoney(paymentOf(row)) }}
								</td>
								<td class="text-right"><strong>{{ money(row.balance) }}</strong></td>
							</tr>
							<tr v-if="!entries.length">
								<td colspan="7" class="text-center text-muted" style="padding:24px">
									No entries in this range.
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<div class="col-md-12" v-if="slips.length">
			<div class="panel">
				<div class="panel-body" style="background:none">
					<h5>Slips</h5>
					<table class="table table-bordered table-hover table-striped display">
						<thead class="table-font" style="background:#475c78;color:#fff">
							<th>#</th>
							<th>Slip no.</th>
							<th>Period</th>
							<th class="text-right">Payable</th>
							<th class="text-right">Paid</th>
							<th>Status</th>
							<th></th>
						</thead>
						<tbody class="table-font">
							<tr v-for="(slip, index) in slips" :key="slip.id">
								<td>{{ index + 1 }}</td>
								<td>{{ slip.slip_no || '—' }}</td>
								<td>{{ formatDate(slip.period_start) }} – {{ formatDate(slip.period_end) }}</td>
								<td class="text-right">{{ moneyShort(slip.amount_payable) }}</td>
								<td class="text-right">{{ moneyShort(slip.paid_amount) }}</td>
								<td><span :class="slipStatusClass(slip.status)">{{ slipStatusLabel(slip.status) }}</span></td>
								<td>
									<button class="badge bg-primary" @click="openSlip(slip)">Open</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!--
			Every advance, open and closed. The outstanding figure above answers
			"does he owe anything now"; this answers "has he ever taken one",
			which a zero balance hides.
		-->
		<div class="col-md-12">
			<div class="panel">
				<div class="panel-body" style="background:none">
					<h5>Advances &amp; loans</h5>
					<table class="table table-bordered table-hover table-striped display">
						<thead class="table-font" style="background:#475c78;color:#fff">
							<th>#</th>
							<th>Kind</th>
							<th>Given on</th>
							<th class="text-right">Amount</th>
							<th class="text-right">Recovered</th>
							<th class="text-right">Outstanding</th>
							<th>Status</th>
							<th>Note</th>
						</thead>
						<tbody class="table-font">
							<tr v-for="(row, index) in advances" :key="row.id" :class="{ settled: row.outstanding <= 0 }">
								<td>{{ index + 1 }}</td>
								<td>
									<span :class="row.kind === 'loan' ? 'badge bg-info text-dark' : 'badge bg-secondary'">
										{{ row.kind === 'loan' ? 'Loan' : 'Advance' }}
									</span>
								</td>
								<td>{{ formatDate(row.issued_on) }}</td>
								<td class="text-right">{{ moneyShort(row.principal) }}</td>
								<!-- Posted above as a Payment when given; no slip recovers it. -->
								<template v-if="row.settle_via === 'ledger'">
									<td class="text-right text-muted">—</td>
									<td class="text-right text-muted">—</td>
									<td>In ledger (see Payment above)</td>
								</template>
								<template v-else>
									<td class="text-right">{{ moneyShort(row.recovered) }}</td>
									<td class="text-right">
										<strong :class="{ 'text-danger': row.outstanding > 0 }">
											{{ moneyShort(row.outstanding) }}
										</strong>
									</td>
									<td>{{ advanceStatusLabel(row.status) }}</td>
								</template>
								<td class="text-muted">{{ row.note || '' }}</td>
							</tr>
							<tr v-if="!advances.length">
								<td colspan="8" class="text-center text-muted" style="padding:18px">
									Never taken an advance.
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { payLedgerService, advanceService, errorMessage } from './payrollService'
import payrollFormat from './payrollFormat'

const ADVANCE_STATUS = {
	open: 'Open',
	closed: 'Settled',
	on_hold: 'On hold',
	written_off: 'Written off',
}

const ENTRY_LABELS = {
	opening: 'Opening',
	salary_earned: 'Salary',
	payment_made: 'Payment given',
	adjustment: 'Adjustment',
}

/**
 * One person's account.
 *
 * Entry types are names, not numbers — the old ledger stored trantype 34 and 4
 * and the screen had to hardcode what they meant.
 */
export default {
	name: 'PayrollEmployeeLedgerDetail',
	mixins: [payrollFormat],
	data() {
		return {
			employee: {},
			entries: [],
			slips: [],
			advances: [],
			salaryBalance: 0,
			advanceOutstanding: 0,
			// A year back by default: this is an account, not a month's report.
			fromdate: new Date(new Date().setFullYear(new Date().getFullYear() - 1))
				.toISOString()
				.slice(0, 10),
			todate: new Date().toISOString().slice(0, 10),
			error: '',
		}
	},
	mounted() {
		this.refresh()
		this.loadAdvances()
	},
	methods: {
		entryLabel(type) {
			return ENTRY_LABELS[type] || type
		},
		/**
		 * Salary is what the company credits (owes), Payment what it debits
		 * (hands over). An adjustment belongs with what it reverses, shown
		 * negative there: a cancelled slip takes salary back, a reversed payment
		 * takes a payment back. Read by direction alone, a cancelled salary would
		 * look like money paid.
		 */
		reverses(row) {
			if (row.entry_type !== 'adjustment') return null
			if (row.source_table === 'pay_slips') return 'salary'
			if (row.source_table === 'pay_payments') return 'payment'
			return null
		},
		salaryOf(row) {
			const reversed = this.reverses(row)
			if (reversed === 'salary') return -Number(row.amount)
			if (reversed === 'payment') return null
			return row.direction === 'credit' ? Number(row.amount) : null
		},
		paymentOf(row) {
			const reversed = this.reverses(row)
			if (reversed === 'payment') return -Number(row.amount)
			if (reversed === 'salary') return null
			return row.direction === 'debit' ? Number(row.amount) : null
		},
		signedMoney(value) {
			if (value === null) return ''
			return value < 0 ? `−${this.money(-value)}` : this.money(value)
		},
		advanceStatusLabel(status) {
			return ADVANCE_STATUS[status] || status
		},
		/**
		 * Every advance, not only the open ones — a settled advance drops to zero
		 * outstanding and would otherwise vanish from this page entirely.
		 * Loaded once; the date filter above applies to the statement, not to
		 * money somebody still owes.
		 */
		loadAdvances() {
			advanceService
				.list({ employee_id: this.$route.params.employeeId, status: '' })
				.then((data) => {
					this.advances = (data && data.data) || []
				})
				.catch(() => {})
		},
		refresh() {
			this.error = ''
			payLedgerService
				.statement(this.$route.params.employeeId, {
					from_date: this.fromdate,
					to_date: this.todate,
				})
				.then((data) => {
					this.employee = data.employee || {}
					this.entries = data.entries || []
					this.slips = data.slips || []
					this.salaryBalance = data.salary_balance || 0
					this.advanceOutstanding = data.advance_outstanding || 0
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load that statement.')
				})
		},
		openSlip(slip) {
			this.$router.push({ name: 'payroll-slip', params: { id: slip.id } })
		},
		back() {
			this.$router.push({ name: 'payroll-ledger' })
		},
	},
}
</script>

<style scoped>
.text-right {
	text-align: right;
}
.text-center {
	text-align: center;
}
/* Settled advances stay listed but recede — history, not a live obligation. */
.settled td {
	opacity: 0.6;
}
</style>
